import React, { useEffect, useState } from 'react'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  useTheme
} from '@mui/material'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from '../features-tab/style'
import { useTenantFormContext } from '../tenant-form-context'
import { Controller } from 'react-hook-form'

const SkillsPracticeSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant, isEdit, form } = useTenantFormContext()
  const { watch, setValue } = form

  const [isLoading, setIsLoading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [modulesLoading, setModulesLoading] = useState(false)
  const [availableModules, setAvailableModules] = useState([])
  const [selectedModuleIds, setSelectedModuleIds] = useState([])

  const skillsPractice = watch('features.skillsPractice')
  const isEnabled = skillsPractice?.enabled ?? false

  useEffect(() => {
    if (!isEdit) {
      setValue('features.skillsPractice', { enabled: false })
      return
    }
    const enabled = tenant?.features?.skillsPractice?.enabled ?? false
    setValue('features.skillsPractice', { enabled })
    setSelectedModuleIds(tenant?.features?.skillsPractice?.modulesIds ?? [])
  }, [tenant, isEdit, setValue])

  const handleSwitch = () => {
    setValue('features.skillsPractice', { ...skillsPractice, enabled: !isEnabled })
  }

  const handleSkillsPracticeSettings = () => setIsCollapsed(prev => !prev)

  const [tenantUpdate, getModules] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (updatedTenant) => {
        BannerStatic.show({ label: 'AI Skills Practice configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(updatedTenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the AI Skills Practice configuration',
          isError: true
        })
        setIsLoading(false)
      }
    },
    {
      eventName: 'adminSkillsPracticeList',
      onSuccess: ({ modules } = {}) => {
        const activeModules = (modules || []).filter(m => m.status === 'active')
        setAvailableModules(activeModules)
        setModulesLoading(false)
      },
      onError: () => {
        BannerStatic.show({ label: 'Failed to load Skills Practice modules.', isError: true })
        setModulesLoading(false)
      }
    }
  ])

  useEventsOnViewLoad(() => {
    if (isEdit) {
      setModulesLoading(true)
      getModules.trigger()
    }
  }, [isEdit, getModules])

  const handleModuleToggle = (moduleId) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    )
  }

  const handleSave = () => {
    setIsLoading(true)
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        skillsPractice: {
          enabled: isEnabled,
          modulesIds: isEnabled ? selectedModuleIds : []
        }
      }
    })
  }

  const renderModuleSelection = () => {
    if (modulesLoading) {
      return (
        <Box display='flex' justifyContent='center' py={3}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (availableModules.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ mt: spacing(2), mb: spacing(1) }}>
          No modules available. Create modules in the Skills Practice tab first.
        </Typography>
      )
    }

    return (
      <Box mt={spacing(2)} mb={spacing(1)}>
        <Typography variant='body1' sx={{ fontWeight: 500, mb: spacing(1) }}>
          Modules
        </Typography>
        {availableModules.map((module) => (
          <ListItem
            key={module.pk}
            dense
            button
            onClick={() => handleModuleToggle(module.pk)}
            data-test={`skills-practice-module-checkbox-${module.pk}`}
          >
            <Checkbox
              edge='start'
              checked={selectedModuleIds.includes(module.pk)}
              tabIndex={-1}
              disableRipple
              color='primary'
            />
            <ListItemText
              primary={module.title}
              secondary={`SCORM Course Id: ${module.scormCourseId}`}
              secondaryTypographyProps={{ noWrap: true, style: { maxWidth: 400 } }}
            />
          </ListItem>
        ))}
        {selectedModuleIds.length === 0 && (
          <Typography variant='subtitle2' color='warning.main' sx={{ mt: spacing(1), display: 'block' }}>
            Please select at least one module to save.
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius={1}
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
            <Typography variant='body2' sx={{ mt: spacing(3.5) }}>
              AI Skills Practice
            </Typography>
            <Box display='flex' justifyContent='flex-end' alignItems='center' padding={spacing(1)}>
              {isEnabled && !isLoading && isEdit && (
                <>
                  <Box sx={{ ':hover': { cursor: 'pointer' } }}>
                    <List component='nav' aria-label='Modules settings'>
                      <ListItem
                        onClick={handleSkillsPracticeSettings}
                        aria-haspopup='true'
                        aria-controls='skills-practice-settings-menu'
                        aria-label='Modules Settings'
                      >
                        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body2'>Modules Settings</Typography>
                          {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                  <Divider variant='middle' flexItem orientation='vertical' sx={{ mx: spacing(2), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
                </>
              )}
              <Controller
                name='features.skillsPractice.enabled'
                control={form.control}
                render={({ field }) => (
                  <Switch
                    color='primary'
                    checked={!!field.value}
                    onChange={handleSwitch}
                    disabled={isLoading}
                    data-test='skills-practice-product-toggle'
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Collapse in={isCollapsed}>
            {isEnabled && !isLoading && isEdit && renderModuleSelection()}
          </Collapse>

          {isEdit && (
            <Box>
              <Divider style={{ marginBottom: spacing(1) }} />
              <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
                <ActionButton
                  onClick={handleSave}
                  disabled={isLoading || (isEnabled && selectedModuleIds.length === 0)}
                  variant='primary'
                  dataTest='skills-practice-save-button'
                  style={{ height: 'min-content' }}
                >
                  Save
                </ActionButton>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default SkillsPracticeSettings
